import React, { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { trackWord, untrackWord, setWordStrength } from '@/api/words'
import { LoadingMessage } from '@/components/ui/LoadingMessage'
import { ChevronLeft, ChevronRight } from '@/components/ui/icons'
import { GrammarTags } from '@/components/ui/GrammarTags'
import { ProvenanceMarker } from '@/components/ui/ProvenanceMarker'
import { SourceBadge, SourceBadgeList } from '@/components/ui/SourceBadge'
import { WiktionaryCredit } from '@/components/word/WiktionaryCredit'
import { useDismiss } from '@/hooks/useDismiss'
import { computePopoverPosition } from '@/utils/popover'
import { cn } from '@/utils/cn'
import { motion } from '@/utils/motion'
import { getFormTags, ordinalSuffix, CASE_ORDER, CASE_LABELS } from '@/utils/grammar'
import { KNOWN_LEVELS } from '@/utils/strengthLevels'
import { senseSources } from '@/utils/provenance'
import type { WordLookupResult, Word, WordFormInfo, KnownLevel } from '@/types/word'
import type { CustomFlashcard } from '@/types/customFlashcard'
import { focusRing, softFill } from '@/components/ui/buttonStyles'
import { SheetHandle, SheetPortal, sheetSafeArea, sheetSurface } from '@/components/ui/Sheet'
import { useSheetAnchor } from '@/hooks/useSheetAnchor'
import { useIsWide } from '@/hooks/useMediaQuery'
import { SIDE_PANEL_LEFT, SIDE_PANEL_TOP_MIN, SIDE_PANEL_WIDTH, sidePanelTop } from '@/utils/sidePanel'

interface WordPopoverProps {
  clickedWord: string
  sentence: string
  result: WordLookupResult | null
  loading: boolean
  anchorRect: DOMRect | null
  languageId: number
  readingId?: number
  readingTitle?: string
  customCard?: CustomFlashcard | null
  onAddCustom?: () => void
  onClose: () => void
  onTracked?: (lemma: string, surface: string) => void
  onUntracked?: (lemma: string, surface: string) => void
}

export function WordPopover({ clickedWord, sentence, result, loading, anchorRect, languageId, readingId, readingTitle, customCard, onAddCustom, onClose, onTracked, onUntracked }: WordPopoverProps) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const isWide = useIsWide()
  const [position, setPosition] = useState<React.CSSProperties>({ visibility: 'hidden', top: -9999, left: -9999 })

  useDismiss(popoverRef, onClose, anchorRect !== null, { ignore: '[data-word]' })
  useSheetAnchor(anchorRect !== null, anchorRect, popoverRef)

  // Re-run as the content settles: a panel sized for "Looking up word…" would sit in
  // the wrong place once the entry arrives.
  useEffect(() => {
    if (!anchorRect || !popoverRef.current) return
    const height = popoverRef.current.offsetHeight
    setPosition(
      isWide
        ? {
            left: SIDE_PANEL_LEFT,
            top: sidePanelTop(anchorRect, height, window.innerHeight),
            width: SIDE_PANEL_WIDTH,
            maxHeight: `calc(100vh - ${SIDE_PANEL_TOP_MIN + 16}px)`,
            visibility: 'visible',
          }
        : computePopoverPosition(anchorRect, height),
    )
  }, [isWide, anchorRect, loading, result])

  useEffect(() => {
    // A side panel arrives from the gutter it lives in; a popover just fades up.
    if (anchorRect && popoverRef.current) {
      void motion.enter(popoverRef.current, { dx: isWide ? -24 : 0, ms: isWide ? 220 : 180 })
    }
  }, [anchorRect, isWide])

  if (!anchorRect) return null

  const words = result?.words ?? []
  const found = result?.found && words.length > 0

  return (
    <SheetPortal>
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Word details"
      className={cn(
        'fixed z-50 overflow-y-auto border border-border-divider bg-card',
        isWide
          ? 'rounded-xl shadow-xl'
          : cn('sm:w-80 sm:max-w-80 max-h-[70vh] rounded-lg shadow-lg', sheetSurface, sheetSafeArea),
      )}
      style={position}
    >
      <SheetHandle />
      {loading && <LoadingMessage className="p-4">Looking up word…</LoadingMessage>}
      {!loading && customCard && <CustomCardEntry card={customCard} />}
      {!loading && result && !found && !customCard && (
        <div className="p-4">
          <p className="text-sm text-muted font-light">
            {result.proper_noun
              ? 'Proper noun — a name, so no definition.'
              : 'Word not found in dictionary.'}
          </p>
          {onAddCustom && (
            <button
              type="button"
              onClick={onAddCustom}
              className={cn('mt-3 w-full min-h-11 text-xs font-normal rounded-md transition-colors duration-200', softFill, focusRing)}
            >
              Add custom flashcard
            </button>
          )}
        </div>
      )}
      {!loading && found && (
        <WordContent
          words={words}
          clickedWord={clickedWord}
          sentence={sentence}
          languageId={languageId}
          readingId={readingId}
          readingTitle={readingTitle}
          onTracked={onTracked}
          onUntracked={onUntracked}
        />
      )}
    </div>
    </SheetPortal>
  )
}

// ── Custom card entry ───────────────────────────────────────────────────────

function CustomCardEntry({ card }: { card: CustomFlashcard }) {
  return (
    <div className="p-4 pb-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-serif text-lg font-medium text-heading leading-tight truncate">
          {card.lemma}
        </h3>
        <ProvenanceMarker className="mt-1" />
      </div>
      {(card.part_of_speech || card.gender) && (
        <p className="text-xs text-muted font-light mt-0.5">
          {card.part_of_speech && (
            <span className="font-medium text-heading">{card.part_of_speech}</span>
          )}
          {card.gender && <span className="ml-1 text-accent">{card.gender}</span>}
        </p>
      )}
      <p className="text-sm text-body mt-2">{card.translation}</p>
      {card.example_sentence && (
        <p className="text-xs text-muted font-light italic mt-2">{card.example_sentence}</p>
      )}
      {card.notes && (
        <p className="text-xs text-muted font-light mt-1">{card.notes}</p>
      )}
    </div>
  )
}

// ── Main content ────────────────────────────────────────────────────────────

function PopoverAction({ onClick, disabled, title, children }: {
  onClick?: () => void
  disabled?: boolean
  title?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn('flex-1 min-h-11 text-xs font-normal rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-default', softFill, focusRing)}
    >
      {children}
    </button>
  )
}

function WordContent({ words, clickedWord, sentence, languageId, readingId, readingTitle, onTracked, onUntracked }: {
  words: Word[]
  clickedWord: string
  sentence: string
  languageId: number
  readingId?: number
  readingTitle?: string
  onTracked?: (lemma: string, surface: string) => void
  onUntracked?: (lemma: string, surface: string) => void
}) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const word = words[currentIndex]
  const total = words.length

  // Tracking is per interpretation — each part of speech is its own word.
  // Overrides hold this session's changes on top of the looked-up flags.
  const [trackedOverrides, setTrackedOverrides] = useState<Map<number, boolean>>(new Map())
  const [masteredIds, setMasteredIds] = useState<Set<number>>(new Set())
  const [choosingLevel, setChoosingLevel] = useState(false)
  const [saving, setSaving] = useState(false)

  const isMastered = (word.known || masteredIds.has(word.id)) && trackedOverrides.get(word.id) !== false
  const isTracked = trackedOverrides.get(word.id) ?? (word.tracked || isMastered)

  function showInterpretation(index: number) {
    setChoosingLevel(false)
    setCurrentIndex(index)
  }

  function handleTrack() {
    setSaving(true)
    trackWord(languageId, word.id, clickedWord, sentence)
      .then(() => markTracked())
      .finally(() => setSaving(false))
  }

  // Untracking discards the word's review progress.
  function handleUntrack() {
    setSaving(true)
    untrackWord(languageId, word.id)
      .then(() => {
        setTrackedOverrides((prev) => new Map(prev).set(word.id, false))
        onUntracked?.(word.lemma.toLowerCase(), clickedWord.toLowerCase())
      })
      .finally(() => setSaving(false))
  }

  // Already tracked? Only the schedule changes. Otherwise track at the chosen level.
  function handleKnown(level: KnownLevel) {
    setSaving(true)
    const request = isTracked
      ? setWordStrength(languageId, word.id, level)
      : trackWord(languageId, word.id, clickedWord, sentence, level)

    request
      .then(() => {
        markTracked()
        if (level === 'mastered') setMasteredIds((prev) => new Set(prev).add(word.id))
      })
      .finally(() => {
        setSaving(false)
        setChoosingLevel(false)
      })
  }

  function markTracked() {
    setTrackedOverrides((prev) => new Map(prev).set(word.id, true))
    onTracked?.(word.lemma.toLowerCase(), clickedWord.toLowerCase())
  }

  return (
    <div>
      <WordEntry
        word={word}
        clickedWord={clickedWord}
        languageId={languageId}
        readingId={readingId}
        readingTitle={readingTitle}
      />

      {/* Actions */}
      <div className="px-4 pb-4 pt-1 flex items-center gap-2">
        {isMastered && (
          <>
            <PopoverAction disabled>Mastered</PopoverAction>
            <PopoverAction onClick={handleUntrack} disabled={saving} title="Stop tracking this word">
              {saving ? 'Saving...' : 'Untrack'}
            </PopoverAction>
          </>
        )}

        {!isMastered && choosingLevel && KNOWN_LEVELS.map(({ level, label, hint }) => (
          <PopoverAction key={level} onClick={() => handleKnown(level)} disabled={saving} title={hint}>
            {label}
          </PopoverAction>
        ))}

        {!isMastered && !choosingLevel && (
          <>
            <PopoverAction
              onClick={isTracked ? handleUntrack : handleTrack}
              disabled={saving}
              title={isTracked ? 'Stop tracking this word' : undefined}
            >
              {saving ? 'Saving...' : isTracked ? 'Untrack' : 'Track Word'}
            </PopoverAction>
            <PopoverAction onClick={() => setChoosingLevel(true)} disabled={saving}>
              I know this
            </PopoverAction>
          </>
        )}
      </div>

      {/* Interpretation navigation */}
      {total > 1 && (
        <div className="flex items-center justify-between px-4 pb-3 pt-0 border-t border-border-divider">
          <button
            onClick={() => showInterpretation(currentIndex - 1)}
            disabled={currentIndex === 0}
            className="flex items-center justify-center min-h-11 min-w-11 text-muted hover:text-accent transition-colors duration-200 disabled:opacity-30 disabled:cursor-default"
            aria-label="Previous interpretation"
          >
            <ChevronLeft />
          </button>
          <span className="text-[10px] text-muted font-light tracking-wide">
            {currentIndex + 1} of {total} interpretations
          </span>
          <button
            onClick={() => showInterpretation(currentIndex + 1)}
            disabled={currentIndex === total - 1}
            className="flex items-center justify-center min-h-11 min-w-11 text-muted hover:text-accent transition-colors duration-200 disabled:opacity-30 disabled:cursor-default"
            aria-label="Next interpretation"
          >
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Single word entry ────────────────────────────────────────────────────────

const MAX_POPOVER_SENSES = 3

function WordEntry({ word, clickedWord, languageId, readingId, readingTitle }: {
  word: Word
  clickedWord: string
  languageId: number
  readingId?: number
  readingTitle?: string
}) {
  const [expanded, setExpanded] = useState(false)

  const isLemma = clickedWord.toLowerCase() === word.lemma.toLowerCase()
  const formTags = getFormTags(word)
  const formTranslation = word.form?.form_translation
  const primaryTranslation = formTranslation
    ?? word.senses.flatMap((s) => s.translations.en ?? []).slice(0, 3).join(', ')
    ?? null

  const visibleSenses = word.senses.slice(0, MAX_POPOVER_SENSES)
  const hiddenCount = word.senses.length - visibleSenses.length

  // The sense list only renders when there is more than one sense, and it is
  // capped, so a source can go unshown. The headline carries just the sources
  // the list does not already show — otherwise the same word gets the same
  // badge twice, and a headline built from a form translation (whose provenance
  // is the WordForm, not a sense) gets badged on the strength of another sense.
  const shownInSenseList = visibleSenses.length > 1 ? senseSources(visibleSenses) : []
  const headlineSources = senseSources(word.senses).filter((s) => !shownInSenseList.includes(s))

  function handleSeeDetails() {
    if (readingId) {
      sessionStorage.setItem('word_detail_back', JSON.stringify({
        readingId,
        readingTitle: readingTitle ?? '',
        scrollY: window.scrollY,
      }))
    }
  }

  return (
    <div className="p-4 pb-3">
      {/* Header */}
      <div className="min-w-0">
        <h3 className="font-serif text-lg font-medium text-heading leading-tight truncate">
          {isLemma ? word.lemma : clickedWord}
        </h3>
        <p className="text-xs text-muted font-light mt-0.5">
          {!isLemma && <span className="text-body">{word.lemma} &middot; </span>}
          <span className="font-medium text-heading">{word.part_of_speech}</span>
          {word.grammatical_info.gender && (
            <span className="ml-1 text-accent">{word.grammatical_info.gender}</span>
          )}
        </p>
      </div>

      {/* Grammatical form tags */}
      {formTags.length > 0 && !isLemma && (
        <div className="mt-2">
          <GrammarTags tags={formTags} />
        </div>
      )}

      {/* Translation — the headline collapses several senses into one line, and
          the sense list below is capped, so warn if ANY sense was AI-guessed. */}
      {primaryTranslation && (
        <p className="text-sm text-body mt-2">
          {primaryTranslation}
          <SourceBadgeList sources={headlineSources} className="ml-1.5" />
        </p>
      )}

      {/* Senses (capped for popover) */}
      {visibleSenses.length > 1 && (
        <div className="mt-2 space-y-1">
          {visibleSenses.map((sense) => {
            const translations = sense.translations.en ?? []
            return (
              <p key={sense.id} className="text-xs text-muted font-light">
                <span className="text-label mr-1">{sense.sense_index + 1}.</span>
                {translations.length > 0
                  ? translations.join(', ')
                  : sense.definition && <span className="italic">{sense.definition}</span>
                }
                {(translations.length > 0 || sense.definition) && (
                  <SourceBadge source={sense.source} className="ml-1.5" />
                )}
              </p>
            )
          })}
          {hiddenCount > 0 && (
            <p className="text-xs text-muted/60 font-light italic">
              +{hiddenCount} more
            </p>
          )}
        </div>
      )}

      <WiktionaryCredit lemma={word.lemma} senses={word.senses} className="mt-2" />

      {/* All forms toggle */}
      <div className="flex items-center gap-3 mt-2">
        {word.forms.length > 1 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center min-h-11 text-xs text-muted font-light hover:text-accent transition-colors duration-200"
          >
            {expanded ? 'Hide forms' : 'All forms'}
          </button>
        )}
        <Link
          href={`/languages/${languageId}/words/${word.id}`}
          onClick={handleSeeDetails}
          className="inline-flex items-center min-h-11 text-xs text-muted font-light hover:text-accent transition-colors duration-200 ml-auto"
        >
          See details
        </Link>
      </div>

      {expanded && <ParadigmTable forms={word.forms} />}
    </div>
  )
}

// ── Paradigm table ──────────────────────────────────────────────────────────

function ParadigmTable({ forms }: { forms: WordFormInfo[] }) {
  const grouped = groupForms(forms)
  if (grouped.length === 0) return null

  return (
    <div className="mt-2 pt-2 border-t border-border-divider space-y-2.5">
      {grouped.map(([label, entries]) => (
        <div key={label}>
          <p className="text-[10px] text-muted font-medium uppercase tracking-wider mb-1">{label}</p>
          <div className="space-y-px">
            {entries.map((f) => (
              <div key={f.grammatical_slot} className="flex items-baseline justify-between text-xs font-light py-px">
                <span className="text-body">{f.surface}</span>
                <span className="text-muted text-[10px]">{shortSlotLabel(f)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function groupForms(forms: WordFormInfo[]): [string, WordFormInfo[]][] {
  const byCase = new Map<string, WordFormInfo[]>()
  const uncategorized: WordFormInfo[] = []

  for (const f of forms) {
    const caseVal = f.grammatical_info.case
    if (caseVal && CASE_LABELS[caseVal]) {
      const existing = byCase.get(caseVal) ?? []
      existing.push(f)
      byCase.set(caseVal, existing)
    } else {
      uncategorized.push(f)
    }
  }

  const groups: [string, WordFormInfo[]][] = []

  if (byCase.size > 0) {
    for (const c of CASE_ORDER) {
      const entries = byCase.get(c)
      if (entries) groups.push([CASE_LABELS[c], entries])
    }
  }

  if (uncategorized.length > 0) {
    const byTense = new Map<string, WordFormInfo[]>()
    const rest: WordFormInfo[] = []

    for (const f of uncategorized) {
      const tense = f.grammatical_info.tense
      if (tense) {
        const existing = byTense.get(tense) ?? []
        existing.push(f)
        byTense.set(tense, existing)
      } else {
        rest.push(f)
      }
    }

    for (const [tense, entries] of byTense) {
      groups.push([tense.charAt(0).toUpperCase() + tense.slice(1), entries])
    }

    if (rest.length > 0 && groups.length > 0) {
      groups.push(['Other', rest])
    } else if (rest.length > 0) {
      groups.push(['All forms', rest])
    }
  }

  return groups
}

function shortSlotLabel(f: WordFormInfo): string {
  const info = f.grammatical_info
  const parts: string[] = []
  if (info.number) parts.push(info.number)
  if (info.definiteness === 'definite') parts.push('definite')
  if (info.person) parts.push(`${info.person}${ordinalSuffix(info.person)} p.`)
  if (info.mood && info.mood !== 'indicative') parts.push(info.mood)
  return parts.join(', ') || f.grammatical_slot
}

